import { safeJson } from "src/app/helpers/Index";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import React from "react";
import { Team } from "src/app/interfaces/Index";
import { ApiFootballService } from "src/app/services/ApiFootballService";
import { PlayerService } from "src/app/services/Index";
import { colours } from "src/app/Styles";
import "./SquadImport.css";

type Props = {
	onHide: () => void;
	onImported: (done: number, failed: number) => void;
	team: Team;
	visible: boolean;
};
type State = {
	progress: string;
	searchMode: string;
	searchResults: any[];
	searchText: string;
	squad: any[];
	stage: string;
};

/** Pulls a club squad from API-Football and bulk-creates players (as Inactive). */
export class SquadImport extends React.Component<Props, State> {

	constructor(props: Props) {
		super(props);
		this.state = { progress: "", searchMode: "name", searchResults: [], searchText: "", squad: [], stage: "search" };
	}

	componentDidUpdate(prev: Props) {
		if (this.props.visible && !prev.visible) {
			this.setState({ progress: "", searchMode: "name", searchResults: [], searchText: this.props.team.teamName || "", squad: [], stage: "search" });
			const match = (this.props.team.badgeUrl || "").match(/teams\/(\d+)\.png/);
			if (match) this.loadSquad(Number(match[1]));
		}
	}

	positionFromApi(position: string) {
		switch (position) {
			case "Goalkeeper": return 0;
			case "Defender": return 10;
			case "Midfielder": return 20;
			default: return 30;
		}
	}

	async search() {
		if (!this.state.searchText) return;
		if (this.state.searchMode === "id") {
			const id = parseInt(this.state.searchText);
			if (!id || id < 1) {
				this.setState({ progress: "Enter a numeric API-Football team id." });
				return;
			}
			this.loadSquad(id);
			return;
		}
		this.setState({ progress: "Searching\u2026" });
		try {
			const res = await safeJson(ApiFootballService.searchTeams(this.state.searchText));
			this.setState({ progress: (res.response || []).length === 0 ? "No clubs found - try a different name." : "", searchResults: (res.response || []).slice(0, 8) });
		} catch {
			this.setState({ progress: "Search failed - check the connection and try again." });
		}
	}

	async loadSquad(apiTeamId: number) {
		this.setState({ progress: "Loading squad\u2026", stage: "loading" });
		try {
			const res = await safeJson(ApiFootballService.getSquad(apiTeamId));
			const squad = res.response?.[0]?.players || [];
			const existingRes = await safeJson(PlayerService.getByTeam(this.props.team.id));
			const existingNames = new Set((Array.isArray(existingRes) ? existingRes : []).map((x: any) => (x.playerName || "").toLowerCase()));
			const rows = squad.map((player: any) => ({
				name: player.name,
				number: player.number || 0,
				photo: player.photo,
				position: player.position,
				exists: existingNames.has((player.name || "").toLowerCase()),
				selected: !existingNames.has((player.name || "").toLowerCase())
			}));
			this.setState({ progress: rows.length === 0 ? "No squad found for that club." : "", squad: rows, stage: rows.length === 0 ? "search" : "preview" });
		} catch {
			this.setState({ progress: "Could not load the squad - try again.", stage: "search" });
		}
	}

	toggleRow(index: number) {
		const squad = [...this.state.squad];
		squad[index] = { ...squad[index], selected: !squad[index].selected };
		this.setState({ squad });
	}

	async runImport() {
		const picked = this.state.squad.filter((x) => x.selected);
		this.setState({ stage: "importing" });
		let done = 0, failed = 0;
		for (const row of picked) {
			this.setState({ progress: `Importing ${done + failed + 1} of ${picked.length}\u2026` });
			const res = await PlayerService.createSingle({
				teamId: this.props.team.id,
				playerName: row.name,
				playerNumber: row.number || 0,
				playerPosition: this.positionFromApi(row.position),
				playerStatus: 20,
				mugshotUrl: row.photo || ""
			} as any);
			if (res.ok) done++; else failed++;
		}
		this.props.onImported(done, failed);
	}

	render() {
		return (
			<Dialog header={`Import Squad \u2014 ${this.props.team.teamName || ""}`} modal onHide={this.props.onHide} style={{ width: 640 }} visible={this.props.visible}>
				{this.state.stage === "search" &&
					<>
						<p style={{ color: "var(--pb-text-muted)", marginTop: 0 }}>Find the club on API-Football to pull their current squad.</p>
						<div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
							<Button className={this.state.searchMode === "name" ? "" : "p-button-outlined"} label="By Name" style={this.state.searchMode === "name" ? { backgroundColor: colours.primary, borderColor: colours.primary } : {}} onClick={() => this.setState({ searchMode: "name", searchText: this.props.team.teamName || "" })}></Button>
							<Button className={this.state.searchMode === "id" ? "" : "p-button-outlined"} label="By ID" style={this.state.searchMode === "id" ? { backgroundColor: colours.primary, borderColor: colours.primary } : {}} onClick={() => this.setState({ searchMode: "id", searchResults: [], searchText: "" })}></Button>
						</div>
						<div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
							<InputText keyfilter={this.state.searchMode === "id" ? "int" : undefined} onChange={(e) => this.setState({ searchText: e.target.value })} onKeyDown={(e) => e.key === "Enter" && this.search()} placeholder={this.state.searchMode === "id" ? "API-Football team id, e.g. 1804" : "Club name"} style={{ flex: 1 }} value={this.state.searchText} />
							<Button icon="pi pi-search" label={this.state.searchMode === "id" ? "Load Squad" : "Search"} style={{ backgroundColor: colours.buttons.add, borderColor: colours.buttons.add }} onClick={() => this.search()}></Button>
						</div>
						{this.state.searchResults.map((result: any) =>
							<div className="import-result" key={`sr-${result.team.id}`} onClick={() => this.loadSquad(result.team.id)}>
								<img alt="" src={result.team.logo} />
								<span>{result.team.name}</span>
								<small>{result.team.country}</small>
							</div>
						)}
					</>
				}
				{this.state.stage === "preview" &&
					<>
						<p style={{ color: "var(--pb-text-muted)", marginTop: 0 }}>
							{this.state.squad.filter((x) => x.selected).length} of {this.state.squad.length} selected.
							Players are imported as <b>Inactive</b> - use the status arrows to set the matchday squad.
							{this.state.squad.some((x) => x.exists) && " Greyed players already exist and are unticked."}
						</p>
						<div className="import-squad-list">
							{this.state.squad.map((row, i) =>
								<div className={`import-player${row.exists ? " exists" : ""}`} key={`ip-${i}`} onClick={() => this.toggleRow(i)}>
									<span className={`import-check${row.selected ? " on" : ""}`}>{row.selected && <i className="pi pi-check" />}</span>
									{row.photo ? <img alt="" src={row.photo} /> : <span className="import-noPhoto" />}
									<b>{row.number || "-"}</b>
									<span style={{ flex: 1 }}>{row.name}</span>
									<small>{row.position}</small>
								</div>
							)}
						</div>
						<Button icon="pi pi-cloud-download" label={`Import ${this.state.squad.filter((x) => x.selected).length} players`} style={{ backgroundColor: colours.buttons.add, borderColor: colours.buttons.add, marginTop: 12, width: "100%" }} onClick={() => this.runImport()}></Button>
					</>
				}
				{this.state.progress && <p style={{ color: "var(--pb-text-muted)", marginBottom: 0, marginTop: 10 }}>{this.state.progress}</p>}
			</Dialog>
		);
	}
}
